import { useState, useEffect } from 'react';
import { apiFetch } from '../api';

const DISPLAY_NAMES = {
  'BTC-USD': 'Bitcoin',
  'ETH-USD': 'Ethereum',
  '^TNX': '10Y Treasury',
};

function StockTickers({ refreshKey }) {
  const [quotes, setQuotes] = useState([]);
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [editSymbols, setEditSymbols] = useState('');

  useEffect(() => {
    async function fetchData() {
      try {
        const [quotesRes, configRes] = await Promise.all([
          apiFetch('/api/tickers/quotes'),
          apiFetch('/api/tickers/config'),
        ]);
        if (quotesRes.ok) setQuotes(await quotesRes.json());
        if (configRes.ok) setConfig(await configRes.json());
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, [refreshKey]);

  function parseQuote(q) {
    if (q.error || !q.price) return null;
    return {
      symbol: q.symbol,
      displayName: DISPLAY_NAMES[q.symbol] || q.name || q.symbol,
      price: q.price,
      change: q.change || 0,
      changePct: q.changePercent || 0,
      currency: 'USD',
    };
  }

  function startEditing() {
    setEditSymbols(config?.symbols?.join(', ') || '');
    setEditing(true);
  }

  async function saveConfig(e) {
    e.preventDefault();
    const symbols = editSymbols.split(',').map((s) => s.trim()).filter(Boolean);
    try {
      await apiFetch('/api/tickers/config', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ symbols }),
      });
      setConfig({ symbols });
      setEditing(false);
      // Trigger refresh
      window.location.reload();
    } catch (err) {
      console.error(err);
    }
  }

  if (loading) return <section className="card"><h2>Markets</h2><p className="loading">Loading...</p></section>;

  return (
    <section className="card tickers-section">
      <div className="goals-header">
        <h2>Markets</h2>
        <button className="manage-btn" onClick={editing ? () => setEditing(false) : startEditing}>
          {editing ? 'Cancel' : 'Edit'}
        </button>
      </div>
      {editing ? (
        <form onSubmit={saveConfig} className="countdown-form">
          <input
            type="text"
            value={editSymbols}
            onChange={(e) => setEditSymbols(e.target.value)}
            className="shopping-input"
            placeholder="AAPL, BTC-USD, ^TNX..."
            autoFocus
          />
          <p className="ticker-help">Comma-separated Yahoo Finance symbols. Crypto: BTC-USD. Treasury: ^TNX</p>
          <button type="submit" className="submit-btn">Save</button>
        </form>
      ) : (
        <div className="ticker-list">
          {quotes.map((q) => {
            const parsed = parseQuote(q);
            if (!parsed) {
              return (
                <div key={q.symbol} className="ticker-item">
                  <span className="ticker-symbol">{q.symbol}</span>
                  <span className="ticker-error">--</span>
                </div>
              );
            }
            const isUp = parsed.change >= 0;
            return (
              <div key={q.symbol} className={`ticker-item ${isUp ? 'up' : 'down'}`}>
                <div className="ticker-left">
                  <span className="ticker-symbol">{parsed.displayName}</span>
                  <span className="ticker-sym-small">{q.symbol}</span>
                </div>
                <div className="ticker-right">
                  <span className="ticker-price">
                    {parsed.currency === 'USD' ? '$' : ''}{parsed.price.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </span>
                  <span className={`ticker-change ${isUp ? 'up' : 'down'}`}>
                    {isUp ? '+' : ''}{parsed.change.toFixed(2)} ({isUp ? '+' : ''}{parsed.changePct.toFixed(2)}%)
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}

export default StockTickers;
